import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Card, CardContent, Button, Alert,
  FormControl, RadioGroup, FormControlLabel, Radio, CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { profileService } from '../../services/profileService';

const ProfileSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [privacyState, setPrivacyState] = useState<'private' | 'anonymous_public'>('private');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    profileService.getMyProfile()
      .then((res) => {
        setPrivacyState(res.data.privacyState || 'private');
      })
      .catch(() => {
        setError('プロフィール情報の取得に失敗しました');
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await profileService.updatePrivacy(privacyState);
      setMessage('公開設定を更新しました');
    } catch (err: any) {
      setError(err.response?.data?.message || '更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ bgcolor: '#0066cc', color: 'white', py: 3 }}>
        <Container maxWidth="sm">
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            プロフィール公開設定
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ py: 4 }}>
        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              公開範囲の設定
            </Typography>

            <FormControl>
              <RadioGroup
                value={privacyState}
                onChange={(e) => setPrivacyState(e.target.value as 'private' | 'anonymous_public')}
              >
                <FormControlLabel
                  value="private"
                  control={<Radio />}
                  label={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VisibilityOff fontSize="small" />
                        <Typography sx={{ fontWeight: 600 }}>非公開</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#666', ml: 3.5 }}>
                        プロフィールは自分だけが閲覧できます。企業からの検索結果に表示されません。
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2, alignItems: 'flex-start' }}
                />
                <FormControlLabel
                  value="anonymous_public"
                  control={<Radio />}
                  label={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Visibility fontSize="small" />
                        <Typography sx={{ fontWeight: 600 }}>匿名公開</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#666', ml: 3.5 }}>
                        氏名・大学名・メールアドレスを非表示にした状態で、企業がプロフィールを閲覧できます。
                        スカウトを受け取ることができます。
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </RadioGroup>
            </FormControl>

            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                fullWidth
              >
                {saving ? '保存中...' : '設定を保存'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/profile/view')}
                fullWidth
              >
                プロフィールを見る
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button variant="text" onClick={() => navigate('/athlete/dashboard')}>
            ダッシュボードに戻る
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ProfileSettingsPage;
